import { useCallback } from 'react';

import type { ObjectiveOrDraft } from '@/types/objectives';

import { useGetUserById } from '@/api/directory';
import { ObjectiveStatus } from '@/constants/ObjectiveStatus';
import { Permission } from '@/constants/Permission';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';

/**
 * What the current user may do in the objectives module. The server narrows every list to the
 * objectives the user sees and says on each one whether they may change it (`canEdit`): a holder
 * who may not edit objectives reads theirs. This hook decides which controls to offer on top of that.
 *
 * - Reading: every employee reads the objectives they hold, without any permission.
 * - Creating: managing others' objectives, or editing one's own.
 * - The pillar summaries: their totals span objectives beyond one's own, so they take the same
 *   permissions as creating.
 * - The whole company: managing every employee's objectives.
 * - Someone else's objectives (choosing the owner, the AI assistant, the employees without an
 *   objective): that, or managing one's team's objectives or performance.
 * - Deleting: every holder of the objective must be within reach. A manager's reach is a
 *   reporting line the client does not hold, so a manager is offered the deletion and the server
 *   settles the shared case; anyone else only deletes an objective they hold alone.
 * - A draft: read within the same reach as a published objective, but only its creator may change
 *   or delete it, which `canEdit` says.
 *
 * Like `useUserPermissions`, everything reads as refused while the permissions load.
 */
export const useObjectiveAbilities = () => {
    const { can, isLoading } = useUserPermissions();
    const { data: currentUser } = useGetUserById({ userId: 'me' });

    const hasCompanyWideReach = can(Permission.ManageAllEmployeesObjectives);
    const canManageOthersObjectives =
        hasCompanyWideReach ||
        can(Permission.ManageTeamObjectives) ||
        can(Permission.ManageTeamPerformance);
    const canEditOwnObjectives = can(Permission.EditOwnObjectives);
    const canCreateObjectives = canManageOthersObjectives || canEditOwnObjectives;
    const canReadPillarSummaries = canCreateObjectives;
    const currentUserId = currentUser?.id;

    const canDelete = useCallback(
        (objective: ObjectiveOrDraft) =>
            objective.canEdit &&
            (objective.status === ObjectiveStatus.DRAFT ||
                canManageOthersObjectives ||
                (objective.participantEmployeeIds.length === 0 &&
                    objective.ownerEmployeeId === currentUserId)),
        [canManageOthersObjectives, currentUserId],
    );

    return {
        isLoading,
        hasCompanyWideReach,
        canManageOthersObjectives,
        canEditOwnObjectives,
        canCreateObjectives,
        canReadPillarSummaries,
        canDelete,
        currentUserId,
    };
};
